import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type { LibraryEntityCount } from '@/hooks/library/types'
import { libraryCompaniesQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'

interface UseCompanyCountArgs {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
  enabled?: boolean
}

const fetchCompanyCount = async (
  orgId: UUID | '',
  accessToken: string,
  withConfirmed: boolean,
  withSuggested: boolean,
): Promise<PaginatedResponse<LibraryEntityCount>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }

  let url = `${API_URL}/api/v1/organizations/${orgId}/entities/count`

  const params = new URLSearchParams()
  params.append('entity_types', 'company')
  params.append('with_confirmed', String(withConfirmed))
  params.append('with_suggested', String(withSuggested))

  const response = await fetch(`${url}?${params.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<LibraryEntityCount>
}

const selectCompanyCount = (data: PaginatedResponse<LibraryEntityCount>) => {
  return data.data.find((item) => item.type === 'company')?.count
}

export const useCompanyCount = ({
  orgId,
  withConfirmed = true,
  withSuggested = false,
  enabled = true,
}: UseCompanyCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: libraryCompaniesQueryKeys.count({
      orgId,
      withConfirmed,
      withSuggested,
    }),
    queryFn: () =>
      fetchCompanyCount(orgId, accessToken, withConfirmed, withSuggested),
    enabled: enabled && !!orgId && !!accessToken,
    select: selectCompanyCount,
  })
}
